import { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import openSocket from "socket.io-client";

import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";

let refreshPromise = null;

const useAuth = () => {
	const history = useHistory();
	const [isAuth, setIsAuth] = useState(false);
	const [loading, setLoading] = useState(true);
	const [user, setUser] = useState({});

	useEffect(() => {
		const requestInterceptor = api.interceptors.request.use(config => {
			const storedToken = localStorage.getItem("token");
			if (storedToken) {
				config.headers["Authorization"] = `Bearer ${JSON.parse(storedToken)}`;
			}
			return config;
		}, error => Promise.reject(error));

		const responseInterceptor = api.interceptors.response.use(
			response => response,
			async error => {
				const originalRequest = error.config || {};
				const message = error?.response?.data?.message || error?.response?.data?.error;
				const expiredAccessToken =
					error?.response?.status === 403 &&
					message === "Invalid token. We'll try to assign a new one on next request";

				if (expiredAccessToken && !originalRequest._retry && originalRequest.url !== "/auth/refresh_token") {
					originalRequest._retry = true;
					if (!refreshPromise) {
						refreshPromise = api.post("/auth/refresh_token")
							.then(({ data }) => {
								localStorage.setItem("token", JSON.stringify(data.token));
								api.defaults.headers.Authorization = `Bearer ${data.token}`;
								return data.token;
							})
							.finally(() => {
								refreshPromise = null;
							});
					}
					const newToken = await refreshPromise;
					originalRequest.headers = originalRequest.headers || {};
					originalRequest.headers.Authorization = `Bearer ${newToken}`;
					return api(originalRequest);
				}

				if (error?.response?.status === 401) {
					localStorage.removeItem("token");
					api.defaults.headers.Authorization = undefined;
					setIsAuth(false);
				}
				return Promise.reject(error);
			}
		);

		return () => {
			api.interceptors.request.eject(requestInterceptor);
			api.interceptors.response.eject(responseInterceptor);
		};
	}, []);

	useEffect(() => {
		const token = localStorage.getItem("token");
		(async () => {
			if (token) {
				try {
					const { data } = await api.post("/auth/refresh_token");
					localStorage.setItem("token", JSON.stringify(data.token));
					api.defaults.headers.Authorization = `Bearer ${data.token}`;
					setIsAuth(true);
					setUser(data.user);
				} catch (err) {
					localStorage.removeItem("token");
					api.defaults.headers.Authorization = undefined;
					setIsAuth(false);
				}
			}
			setLoading(false);
		})();
	}, []);

	useEffect(() => {
		const socket = openSocket(process.env.REACT_APP_BACKEND_URL);

		socket.on("user", data => {
			if (data.action === "update" && data.user.id === user.id) {
				setUser(data.user);
			}
		});

		return () => {
			socket.disconnect();
		};
	}, [user]);

	const handleLogin = async userData => {
		setLoading(true);

		try {
			const { data } = await api.post("/auth/login", userData);
			localStorage.setItem("token", JSON.stringify(data.token));
			api.defaults.headers.Authorization = `Bearer ${data.token}`;
			setUser(data.user);
			setIsAuth(true);
			toast.success(i18n.t("auth.toasts.success"));
			history.push("/tickets");
			setLoading(false);
		} catch (err) {
			toastError(err);
			setLoading(false);
		}
	};

	const handleLogout = async () => {
		setLoading(true);

		try {
			await api.delete("/auth/logout");
			setIsAuth(false);
			setUser({});
			localStorage.removeItem("token");
			api.defaults.headers.Authorization = undefined;
			setLoading(false);
			history.push("/login");
		} catch (err) {
			toastError(err);
			setLoading(false);
		}
	};

	return { isAuth, user, loading, handleLogin, handleLogout };
};

export default useAuth;
